import { z } from 'zod';
import { LEVELS, MODULES } from '../enums';
import { zEmail, zId, zOptText, zPassword, zText, zTimeZone } from './common';

// ---- Autenticación ----
export const loginSchema = z.object({ email: zEmail, password: z.string().min(1, 'Requerido').max(200) });
export const mfaVerifySchema = z.object({ code: z.string().regex(/^\d{6}$/, 'Código de 6 dígitos') });
/** Al iniciar sesión: código de 6 dígitos de la aplicación o un código de respaldo (ABCDE-FGHJK). */
export const mfaLoginSchema = z.object({
  code: z.string().trim().max(20).regex(/^(\d{6}|[A-Za-z0-9]{5}[\s-]?[A-Za-z0-9]{5})$/, 'Código de 6 dígitos o código de respaldo'),
});
export const passwordResetRequestSchema = z.object({ email: zEmail });
export const passwordResetSchema = z.object({ token: z.string().min(20).max(200), password: zPassword });
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: zPassword });
export const accountSignupSchema = z.object({
  firstName: zText(80),
  lastName: zText(80),
  email: zEmail,
  password: zPassword,
  campusId: zId.nullable().optional(),
});

// ---- Organización y campus ----
export const organizationUpdateSchema = z.object({
  name: zText(120),
  timezone: zTimeZone,
  currency: z.string().length(3).toUpperCase(),
  locale: z.enum(['es', 'en']),
  /** Solo se puede desactivar fuera de producción (demostraciones y pruebas). */
  requireStaffMfa: z.boolean().optional(),
});

export const campusSchema = z.object({
  name: zText(80),
  code: z.string().trim().min(1).max(12).toUpperCase(),
  timezone: zTimeZone,
  street: zOptText(200),
  city: zOptText(80),
  state: zOptText(40),
  zip: zOptText(20),
  phone: zOptText(30),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
});

// ---- Cuentas y permisos ----
export const accountCreateSchema = z.object({
  personId: zId,
  email: zEmail,
  password: zPassword,
  isSuperAdmin: z.boolean().default(false),
});

export const accountUpdateSchema = z.object({
  isActive: z.boolean().optional(),
  isSuperAdmin: z.boolean().optional(),
});

export const roleAssignmentSchema = z.object({
  accountId: zId,
  module: z.enum(MODULES),
  level: z.enum(LEVELS),
  campusId: zId.nullable().default(null),
});

export const notificationPreferenceSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  whatsapp: z.boolean(),
});

/** base64url sin relleno (así entrega el navegador las llaves de la suscripción push). */
const b64url = (min: number, max: number) => z.string().regex(/^[A-Za-z0-9_-]+={0,2}$/, 'Llave inválida').min(min).max(max);

/** Lo que entrega el navegador al aceptar notificaciones push (PushSubscription.toJSON()). */
export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: b64url(80, 100), auth: b64url(16, 32) }),
});

export const pushUnsubscribeSchema = z.object({ endpoint: z.string().url().max(1000) });

export const conversationMuteSchema = z.object({ muted: z.boolean() });
